/**
 * "Scrivi a piu' persone": il messaggio di ciascun destinatario e le righe da
 * salvare in whatsapp_scheduled_messages.
 *
 *  - Numero Standard: testo libero, {{nome}} sostituito col nome del Lead.
 *  - Numero Ufficiale: modello approvato; nei campi delle variabili si puo'
 *    scrivere {{nome}} e ogni destinatario riceve il suo. I `components` per
 *    Meta si preparano qui (templateSend.ts, lo stesso codice dell'invio dalla
 *    chat) e il core li spedisce cosi' come sono.
 *
 * Modulo puro, testato in __tests__/bulkMessage.test.ts.
 */
import {
  buildTemplateSendComponents,
  describeTemplate,
  renderTemplatePlainText,
  templateBlockReason,
  HEADER_MEDIA_ID_KEY,
  type TemplateShape,
} from '../templateSend';
import type { BulkChannel } from './bulkSchedule';
import type { BulkRecipient } from './bulkRecipients';

export const NAME_TOKEN = '{{nome}}';
const NAME_RE = /\{\{\s*nome\s*\}\}/gi;
export const MAX_TEXT_LENGTH = 4096;

export function hasNameToken(text: string): boolean {
  return new RegExp(NAME_RE.source, 'i').test(text || '');
}

/**
 * Sostituisce {{nome}}. Senza nome il segnaposto sparisce e la frase resta
 * pulita: "Ciao {{nome}}, come va?" diventa "Ciao, come va?".
 */
export function personalize(text: string, firstName: string): string {
  const name = (firstName || '').trim();
  const out = (text || '').replace(NAME_RE, name);
  if (name) return out;
  return out
    .replace(/[ \t]+([,.;:!?])/g, '$1')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/^[ \t]+|[ \t]+$/gm, '');
}

/** Perche' il testo libero non puo' partire, oppure null. */
export function standardTextBlockReason(text: string): string | null {
  const t = (text || '').trim();
  if (!t) return 'Scrivi il messaggio.';
  if (t.length > MAX_TEXT_LENGTH) return `Il messaggio e' troppo lungo (massimo ${MAX_TEXT_LENGTH} caratteri).`;
  return null;
}

export interface BulkTemplate {
  name: string;
  language: string;
  components: unknown[];
}

/** Variabili del modello per un destinatario: {{nome}} sostituito in ogni campo. */
export function templateVarsFor(vars: Record<string, string>, firstName: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(vars)) out[k] = personalize(v, firstName);
  return out;
}

/**
 * Perche' il modello non puo' partire per tutti, oppure null. Si controlla con
 * un nome di prova e poi senza nome: un campo che contiene solo {{nome}}
 * resterebbe vuoto per chi non ha un nome in scheda, e Meta lo rifiuterebbe.
 */
export function bulkTemplateBlockReason(shape: TemplateShape, vars: Record<string, string>, recipients: BulkRecipient[]): string | null {
  if (vars[HEADER_MEDIA_ID_KEY]) return "Per l'invio a piu' persone l'allegato va indicato con un link https.";
  const base = templateBlockReason(shape, templateVarsFor(vars, 'Mario'));
  if (base) return base;
  const senzaNome = recipients.filter((r) => !r.firstName).length;
  if (senzaNome > 0 && templateBlockReason(shape, templateVarsFor(vars, ''))) {
    return senzaNome === 1
      ? "1 destinatario non ha un nome in scheda: aggiungi del testo oltre a {{nome}} nel campo, o toglilo dalla lista."
      : `${senzaNome} destinatari non hanno un nome in scheda: aggiungi del testo oltre a {{nome}} nel campo, o toglili dalla lista.`;
  }
  return null;
}

export interface TemplatePayload {
  template_name: string;
  language: string;
  components: unknown[];
  rendered_text: string;
}

export function templatePayloadFor(tpl: BulkTemplate, vars: Record<string, string>, firstName: string): TemplatePayload {
  const shape = describeTemplate(tpl.components);
  const v = templateVarsFor(vars, firstName);
  return {
    template_name: tpl.name,
    language: tpl.language,
    components: buildTemplateSendComponents(shape, v),
    rendered_text: renderTemplatePlainText(shape, v),
  };
}

/** Testo che legge un destinatario, per l'anteprima del riepilogo. */
export function previewFor(
  channel: BulkChannel,
  recipient: Pick<BulkRecipient, 'firstName'>,
  message: { text?: string; template?: BulkTemplate | null; vars?: Record<string, string> },
): string {
  if (channel === 'standard') return personalize(message.text || '', recipient.firstName);
  if (!message.template) return '';
  return templatePayloadFor(message.template, message.vars || {}, recipient.firstName).rendered_text;
}

export interface ScheduledRowInput {
  organizationId: string;
  configId: string;
  userId: string;
  bulkSendId: string;
  channel: BulkChannel;
  recipients: BulkRecipient[];
  times: Date[];
  text?: string;
  template?: BulkTemplate | null;
  vars?: Record<string, string>;
}

/** Righe per whatsapp_scheduled_messages, una per destinatario. */
export function buildScheduledRows(p: ScheduledRowInput) {
  return p.recipients.map((r, i) => {
    const base = {
      organization_id: p.organizationId,
      config_id: p.configId,
      created_by: p.userId,
      bulk_send_id: p.bulkSendId,
      chat_jid: r.chatJid,
      recipient_name: r.name,
      client_id: r.clientId,
      scheduled_at: p.times[i].toISOString(),
      status: 'scheduled' as const,
    };
    if (p.channel === 'standard') {
      return { ...base, type: 'text', content: personalize(p.text || '', r.firstName), payload: null };
    }
    const payload = templatePayloadFor(p.template!, p.vars || {}, r.firstName);
    return { ...base, type: 'template', content: payload.rendered_text || payload.template_name, payload };
  });
}
